import Select, { components } from "react-select";
import { Children, FC } from "react";
import colors from "tailwindcss/colors";
import Colors from "../../constants/colors";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

const { ValueContainer, Placeholder, MultiValue, Option } = components;

const CustomValueContainer: FC<any> = ({ children, ...props }) => {
  const { t } = useTranslation("shared");
  const { isMulti, value } = props.selectProps;

  const hasSelectedValues =
    isMulti && (value && Array.isArray(value) ? (value.length > 0 ? true : false) : true);

  if (hasSelectedValues) {
    const text = Array.isArray(value)
      ? value.length === 1
        ? value[0].label
        : t("select.selectedCount", { count: value.length })
      : value?.label || "undefined";
    return (
      <ValueContainer {...props}>
        <Placeholder {...props} isFocused={props.isFocused}>
          {props.selectProps.placeholder}
        </Placeholder>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">{text}</span>
        </div>
      </ValueContainer>
    );
  }

  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {Children.map(children, (child) => (child && child.type !== Placeholder ? child : null))}
    </ValueContainer>
  );
};

const CustomOption: FC<any> = ({ children, isSelected, ...props }) => {
  return (
    <Option {...props}>
      <div className="flex w-full items-center justify-between">
        <span>{children}</span>
        {isSelected && <Check className="h-4 w-4 flex-shrink-0 text-blue-600" />}
      </div>
    </Option>
  );
};

type StyledSelectWoControllerProps = {
  options: { value: number; label: string }[];
  onChange: (e: any) => void;
  className?: string;
  placeholder: string;
  isClearable?: boolean;
  value?: { value: number; label: string } | { value: number; label: string }[];
  isSearchable?: boolean;
  height?: string;
  isDisabled?: boolean;
  isMulti?: boolean;
  defaultValue?: { value: number; label: string } | { value: number; label: string }[];
};

export const StyledSelectIntWoController = ({
  options,
  onChange,
  className,
  placeholder,
  isClearable = true,
  isSearchable = true,
  isDisabled = false,
  height,
  defaultValue,
  value,
  isMulti = false,
}: StyledSelectWoControllerProps) => {
  return (
    <Select
      isDisabled={isDisabled}
      placeholder={placeholder}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isMulti={isMulti}
      components={{
        IndicatorSeparator: () => null,
        ValueContainer: CustomValueContainer,
        MultiValue: isMulti ? () => null : MultiValue,
        Option: isMulti ? CustomOption : Option,
      }}
      hideSelectedOptions={false}
      options={options}
      onChange={onChange}
      className={className}
      menuPortalTarget={document.body}
      value={value}
      defaultValue={defaultValue}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          borderColor: colors.gray[300],
          height: height || "3rem",
          borderRadius: "0.5rem",
        }),
        placeholder: (baseStyles, state) => ({
          ...baseStyles,
          transform: "translateY(0)",
          display: state.hasValue ? "none" : "block",
          fontSize: "0.9rem",
          fontWeight: "400",
          color: Colors.gray400,
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          overflow: "visible",
          marginLeft: "0.4rem",
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
        }),
        multiValue: (baseStyles) => ({
          ...baseStyles,
          backgroundColor: colors.gray[100],
          borderRadius: "0.25rem",
        }),
        multiValueLabel: (baseStyles) => ({
          ...baseStyles,
          color: colors.gray[700],
          fontSize: "0.875rem",
        }),
        multiValueRemove: (baseStyles) => ({
          ...baseStyles,
          color: colors.gray[500],
          ":hover": {
            backgroundColor: colors.red[100],
            color: colors.red[600],
          },
        }),
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
    />
  );
};
